import { useReducer, useCallback, useRef, useEffect } from "react"
import dayjs from "dayjs"
import { calculateMinWidth } from "src/utils/table-render-functions"
import { DATE_FORMAT } from "src/utils/constants"

const EMPTY_ULTIMO = {
    data_referencia: "",
    indice_liquidez: "",
}

const initialState = {
    loading: false,
    rows: [],
    ultimo: EMPTY_ULTIMO,
    alert: { open: false, message: "", type: "" },
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload }
        case "SET_ROWS":
            return { ...state, rows: action.payload }
        case "SET_ULTIMO":
            return { ...state, ultimo: action.payload }
        case "SET_ALERT":
            return { ...state, alert: { ...state.alert, ...action.payload } }
        case "CLOSE_ALERT":
            return { ...state, alert: { ...state.alert, open: false } }
        default:
            return state
    }
}

const ordenaPorDataReferencia = (arr, order = "asc") =>
    [...arr].sort((a, b) => {
        const dateA = a?.DataReferencia
            ? new Date(a.DataReferencia)
            : new Date(0)

        const dateB = b?.DataReferencia
            ? new Date(b.DataReferencia)
            : new Date(0)

        return order === "desc" ? dateB - dateA : dateA - dateB
    })

const getResponseResults = (response) => {
    const body = response?.body

    if (Array.isArray(body)) return body
    if (Array.isArray(body?.results)) return body.results
    if (Array.isArray(body?.data)) return body.data
    if (Array.isArray(body?.data?.results)) return body.data.results

    return []
}

const buildUltimoFromRow = (row = {}) => ({
    data_referencia: row?.DataReferencia ?? "",
    indice_liquidez: row?.IndiceLiquidez ?? "",
})

const removeRowIdentifiers = (row = {}) => {
    const payload = { ...row }

    delete payload.id
    delete payload.ID

    return payload
}

export const useIndiceLiquidezTable = (
    emissaoService,
    cetip,
    dadosEmpresa
) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const loadedRef = useRef(cetip)

    useEffect(() => {
        if (loadedRef.current === cetip) return

        loadedRef.current = cetip

        dispatch({ type: "SET_ROWS", payload: [] })
        dispatch({ type: "SET_ULTIMO", payload: EMPTY_ULTIMO })
        dispatch({ type: "CLOSE_ALERT" })
    }, [cetip])

    const normalizeRows = useCallback((listaResultados = []) => {
        return ordenaPorDataReferencia(listaResultados, "desc").map(
            (obj, index) => ({
                ...obj,
                id: obj?.id ?? obj?.ID ?? index,
            })
        )
    }, [])

    const applyRows = useCallback(
        (listaResultados = []) => {
            const rows = normalizeRows(listaResultados)
            const primeiraLinha = rows[0] || {}

            dispatch({ type: "SET_ROWS", payload: rows })
            dispatch({
                type: "SET_ULTIMO",
                payload: buildUltimoFromRow(primeiraLinha),
            })

            return rows
        },
        [normalizeRows]
    )

    const loadTableRows = useCallback(async () => {
        if (!cetip) {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "CETIP não informado ou inválido.",
                    type: "warning",
                },
            })

            dispatch({ type: "SET_ROWS", payload: [] })
            dispatch({ type: "SET_ULTIMO", payload: EMPTY_ULTIMO })

            return
        }

        dispatch({ type: "SET_LOADING", payload: true })

        try {
            const response = await emissaoService.indiceLiquidez(
                "get",
                `?CETIP=${encodeURIComponent(cetip.toUpperCase())}`
            )

            const listaResultados = getResponseResults(response)

            if (!listaResultados.length) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Não há dados para o CETIP consultado.",
                        type: "warning",
                    },
                })

                dispatch({ type: "SET_ROWS", payload: [] })
                dispatch({ type: "SET_ULTIMO", payload: EMPTY_ULTIMO })

                return
            }

            dispatch({ type: "CLOSE_ALERT" })
            applyRows(listaResultados)
        } catch {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao buscar dados ou CETIP não encontrado.",
                    type: "warning",
                },
            })

            dispatch({ type: "SET_ROWS", payload: [] })
            dispatch({ type: "SET_ULTIMO", payload: EMPTY_ULTIMO })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [emissaoService, cetip, applyRows])

    const addRow = useCallback(async () => {
        const lastItem = state.rows[0]

        const payload = {
            ...(lastItem ? removeRowIdentifiers(lastItem) : {}),
            CETIP: lastItem?.CETIP ?? dadosEmpresa?.CETIP ?? cetip,
            DataReferencia:
                lastItem?.DataReferencia ?? dayjs().format(DATE_FORMAT),
            IndiceLiquidez: lastItem?.IndiceLiquidez ?? 0,
        }

        if (!payload.CETIP) {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "CETIP não informado para adicionar o índice de liquidez.",
                    type: "warning",
                },
            })

            return
        }

        dispatch({ type: "SET_LOADING", payload: true })

        try {
            await emissaoService.indiceLiquidez("post", payload)
            await loadTableRows()
        } catch {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao adicionar novo registro.",
                    type: "error",
                },
            })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [state.rows, cetip, dadosEmpresa, emissaoService, loadTableRows])

    const deleteRow = useCallback(
        async (row) => {
            const id = row?.ID ?? row?.id

            if (!id) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Registro sem ID para exclusão.",
                        type: "warning",
                    },
                })
                return
            }

            dispatch({ type: "SET_LOADING", payload: true })

            try {
                await emissaoService.indiceLiquidez("delete", id)
                await loadTableRows()
            } catch {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao deletar registro.",
                        type: "error",
                    },
                })
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, loadTableRows]
    )

    const updateRow = useCallback(
        async (row) => {
            const id = row?.ID ?? row?.id

            if (!id) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Registro sem ID para atualização.",
                        type: "warning",
                    },
                })

                return row
            }

            dispatch({ type: "SET_LOADING", payload: true })

            try {
                await emissaoService.indiceLiquidez("patch", id, row)
                await loadTableRows()

                return row
            } catch (error) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao atualizar registro.",
                        type: "error",
                    },
                })

                throw error
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, loadTableRows]
    )

    const setAlert = useCallback(
        (payload) => dispatch({ type: "SET_ALERT", payload }),
        []
    )

    const closeAlert = useCallback(() => dispatch({ type: "CLOSE_ALERT" }), [])

    return {
        state: {
            ...state,
            minWidthIndiceLiquidez: calculateMinWidth(
                state.rows,
                "IndiceLiquidez"
            ),
        },
        actions: {
            loadTableRows,
            addRow,
            deleteRow,
            updateRow,
            setAlert,
            closeAlert,
            setRows: (rows) => applyRows(rows),
        },
    }
}